/**
 * Onboarding page — the multi-step guided onboarding wizard.
 *
 * Steps:
 * 1. Welcome & Intro
 * 2. Hierarchy & How it Works
 * 3. Create Organisation
 * 4. Create First Application (Website, Mobile App, etc.)
 * 5. Show API Key (JSON credentials download, Reveal/Hide, copy feedback)
 * 6. Ready to Integrate (Setup Checklist and CTA to dashboard)
 */

import type { FormEvent } from 'react';
import { STORAGE_KEYS } from '../../lib/constants';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../contexts/AuthContext';
import { emitAnalyticsEvent } from '../../lib/analytics';
import { apiFetch, getApiKey, getNetworkErrorMessage, setApiKey } from '../../lib/api';
import type { CreateOrgResponse } from '../../lib/types';
import { AuthLayout } from './AuthLayout';

export function OnboardingPage() {
  const { organisations, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Onboarding step persistence
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ONBOARDING_STEP);
    return saved ? parseInt(saved, 10) : 1;
  });

  // State collected across steps
  const [orgName, setOrgName] = useState('');
  const [appName, setAppName] = useState('');
  const [createdAppId, setCreatedAppId] = useState('');
  const [createdAppName, setCreatedAppName] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState('');

  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track step progression in localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_STEP, currentStep.toString());
  }, [currentStep]);

  // Handle reload/resume state based on active organisations list
  useEffect(() => {
    if (organisations.length > 0) {
      // If we have an organisation, step should be at least Step 4
      if (currentStep < 4) {
        setCurrentStep(4);
      }
      const firstOrg = organisations[0];
      if (firstOrg) {
        setOrgName(firstOrg.name);
      }
    } else {
      // No organisations, step cannot be greater than Step 3
      if (currentStep > 3) {
        setCurrentStep(3);
      }
    }
  }, [organisations, currentStep]);

  const hasTrackedStart = useRef(false);

  useEffect(() => {
    if (currentStep === 1 && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      emitAnalyticsEvent('Onboarding Started');
    }
  }, [currentStep]);

  const handleNext = () => {
    setError(null);
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSkipOnboarding = () => {
    emitAnalyticsEvent('Onboarding Skipped', { currentStep });
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    navigate('/dashboard');
  };

  const handleCreateOrg = async (e: FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const data = await apiFetch<CreateOrgResponse>('/v1/organisations', {
        method: 'POST',
        body: { name: orgName.trim() },
      });

      emitAnalyticsEvent('Organisation Created', { organisationId: data.organisationId });

      // Save initial app info in state
      setCreatedAppId(data.applicationId);
      setCreatedApiKey(data.apiKey);
      setCreatedAppName('Default App');

      // Update API Key in local storage so subsequent dashboard calls work
      setApiKey(data.apiKey, data.applicationId);

      // Refresh User context
      await refreshUser();

      // Advance step
      setCurrentStep(4);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to create organisation.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateApp = async (e: FormEvent) => {
    e.preventDefault();
    const finalAppName = appName.trim();
    if (!finalAppName) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Register custom application
      const data = await apiFetch<{ applicationId: string; name: string; apiKey: string }>(
        '/v1/applications',
        {
          method: 'POST',
          body: { name: finalAppName },
          useApiKey: true,
        },
      );

      emitAnalyticsEvent('Application Created', { applicationId: data.applicationId });

      setCreatedAppId(data.applicationId);
      setCreatedApiKey(data.apiKey);
      setCreatedAppName(data.name);

      // Store key for this active application
      setApiKey(data.apiKey, data.applicationId);

      setCurrentStep(5);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to create application.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyKey = () => {
    if (!createdApiKey) return;
    navigator.clipboard
      .writeText(createdApiKey)
      .then(() => {
        setCopied(true);
        localStorage.setItem(STORAGE_KEYS.SETUP_COPIED_KEY, 'true');
        emitAnalyticsEvent('API Key Copied', { applicationId: createdAppId });
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard unavailable (HTTP context or browser restriction)
        setError('Could not copy to clipboard. Please copy the key manually.');
      });
  };

  const handleDownloadCredentials = () => {
    const creds = {
      organisation: orgName,
      applicationId: createdAppId,
      applicationName: createdAppName,
      apiKey: createdApiKey,
      createdAt: new Date().toISOString(),
      environment: 'staging',
    };

    const blob = new Blob([JSON.stringify(creds, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dpdp_credentials_${createdAppName.toLowerCase().replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    emitAnalyticsEvent('Credentials Downloaded', { applicationId: createdAppId });
  };

  const handleCompleteOnboarding = () => {
    emitAnalyticsEvent('Onboarding Completed');
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    navigate('/dashboard');
  };

  // ---------------------------------------------------------
  // Render Steps
  // ---------------------------------------------------------

  const renderWelcome = () => (
    <AuthLayout title="Welcome to DPDP Consent" subtitle="Become DPDP Compliant in Minutes">
      <div className="space-y-6 text-slate-300">
        <div className="flex justify-center text-5xl">🛡️</div>
        <p className="text-sm leading-relaxed text-slate-400">
          India's <strong>Digital Personal Data Protection (DPDP) Act, 2023</strong> mandates that
          personal data collection must have consent that is{' '}
          <em>free, specific, informed, unconditional, and withdrawable</em>.
        </p>
        <p className="text-sm leading-relaxed text-slate-400">
          Our compliance engine makes notice publishing, consent logging, and audit trail validation
          self-serve and developer-friendly. Let's guide you through the setup.
        </p>
        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={handleSkipOnboarding}
            className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-sm font-semibold text-slate-400 hover:bg-slate-900/60"
          >
            Skip Intro
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg"
          >
            Get Started →
          </button>
        </div>
      </div>
    </AuthLayout>
  );

  const renderConcepts = () => (
    <AuthLayout title="How it Works" subtitle="Under the Hood of the compliance platform">
      <div className="space-y-6 text-slate-300 text-sm">
        <div className="flex justify-center text-5xl">📱</div>
        <div className="space-y-5">
          <div>
            <p className="text-blue-400 font-bold text-sm">1. Workspace Organisation</p>
            <p className="text-slate-400 text-xs mt-1">
              Your corporate environment, billing profile, and team members dashboard.
            </p>
          </div>
          <div>
            <p className="text-blue-400 font-bold text-sm">2. Applications</p>
            <p className="text-slate-400 text-xs mt-1">
              Each product (Web, iOS, Android, Admin portal) has its own independent consents,
              notices, webhooks, and API keys.
            </p>
          </div>
          <div>
            <p className="text-blue-400 font-bold text-sm">3. API Keys &amp; Webhooks</p>
            <p className="text-slate-400 text-xs mt-1">
              Use API keys to authenticate widget integrations. Webhooks push consent events
              downstream to keep systems in sync.
            </p>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-sm font-semibold text-slate-400 hover:bg-slate-900/60"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg"
          >
            Next Step
          </button>
        </div>
      </div>
    </AuthLayout>
  );

  const renderOrgForm = () => (
    <AuthLayout title="Create Your Organisation" subtitle="You'll be the owner of this workspace">
      <form onSubmit={handleCreateOrg} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
            Organisation Name
          </label>
          <input
            type="text"
            placeholder="e.g. Parakrama Technologies"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            disabled={isSubmitting}
            autoFocus
            maxLength={100}
            required
          />
        </div>

        {error && (
          <p className="rounded-lg border border-red-800 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error}
          </p>
        )}

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-sm font-semibold text-slate-400 hover:bg-slate-900/60"
            disabled={isSubmitting}
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !orgName.trim()}
            className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Spinner size="sm" /> : 'Create Organisation'}
          </button>
        </div>
      </form>
    </AuthLayout>
  );

  const renderAppForm = () => (
    <AuthLayout
      title="Create Your First Application"
      subtitle="What product is integrating with us?"
    >
      <form onSubmit={handleCreateApp} className="space-y-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/20 p-4 mb-4 text-xs text-slate-400 leading-relaxed">
          <p className="font-semibold text-slate-300">What is an Application?</p>
          <p className="mt-1">
            Applications represent separate interfaces or platforms (e.g. Website, iOS App, Client
            Portal). Each app gets its own separate API key, consent notices, and audit registers.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
            Application Name
          </label>
          <input
            type="text"
            placeholder="e.g. Company Website"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            disabled={isSubmitting}
            autoFocus
            maxLength={80}
            required
          />
        </div>

        {error && (
          <p className="rounded-lg border border-red-800 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error}
          </p>
        )}

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={() => {
              // Fall back to the Default App created during org setup
              // Read key + app ID from localStorage since they were stored by handleCreateOrg
              const existingKey = getApiKey();
              const existingAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';
              if (existingKey) setCreatedApiKey(existingKey);
              if (existingAppId) setCreatedAppId(existingAppId);
              setCreatedAppName('Default App');
              setCurrentStep(5);
            }}
            className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-sm font-semibold text-slate-400 hover:bg-slate-900/60"
            disabled={isSubmitting}
          >
            Use Default App
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !appName.trim()}
            className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Spinner size="sm" /> : 'Create Application'}
          </button>
        </div>
      </form>
    </AuthLayout>
  );

  const renderCredentials = () => (
    <AuthLayout
      title="Save Your API Credentials"
      subtitle="Store these securely to integrate your app"
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-xs text-yellow-300/90 leading-relaxed">
          <p className="font-bold flex items-center gap-1.5">⚠️ Security Notice</p>
          <p className="mt-1">
            This API Key grants read/write permissions for consent tracking. Show this key only to
            trusted staff, and do not commit it to public version control.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
            Application ID
          </label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 p-3">
            <span className="flex-1 font-mono text-xs text-slate-300 truncate">{createdAppId}</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
            API Key
          </label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 p-3">
            <span className="flex-1 font-mono text-xs text-slate-300 truncate">
              {showKey ? createdApiKey : '••••••••••••••••••••••••••••••••••••••••'}
            </span>
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="text-xs text-slate-500 hover:text-slate-300 px-1"
            >
              {showKey ? 'Hide' : 'Reveal'}
            </button>
            <button
              type="button"
              onClick={handleCopyKey}
              className="text-xs text-blue-400 hover:text-blue-300 font-semibold px-1"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleDownloadCredentials}
            className="flex-1 rounded-lg border border-slate-800 bg-slate-900/30 py-2.5 text-xs font-semibold text-slate-300 hover:bg-slate-900/60"
          >
            Download Credentials JSON
          </button>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={handleNext}
            className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:from-blue-500 hover:to-indigo-500"
          >
            Continue
          </button>
        </div>
      </div>
    </AuthLayout>
  );

  const renderComplete = () => (
    <AuthLayout title="You are Ready!" subtitle="Next steps to go live">
      <div className="space-y-6 text-slate-300 text-sm">
        <div className="flex justify-center text-5xl">🚀</div>
        <p className="text-slate-400 text-xs text-center">
          Onboarding complete! Your organisation and first application have been created
          successfully.
        </p>

        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4 space-y-3">
          <p className="font-semibold text-slate-300 text-xs uppercase tracking-wider">
            Next Recommended Step:
          </p>
          <div className="text-xs text-slate-400 leading-relaxed">
            <p className="font-bold text-slate-200">Create your First Notice</p>
            <p className="mt-1">
              Customise notices in English and Hindi to match the legal purposes of data processing
              inside your product.
            </p>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={handleCompleteOnboarding}
            className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg text-center"
          >
            Enter Dashboard
          </button>
        </div>
      </div>
    </AuthLayout>
  );

  // ---------------------------------------------------------
  // Step Selector
  // ---------------------------------------------------------

  const totalSteps = 6;
  const progressPercent = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-between py-6">
      {/* Progress Bar Header */}
      <div className="max-w-md w-full mx-auto px-6">
        <div className="flex justify-between items-center text-[10px] uppercase font-extrabold tracking-wider text-slate-500 mb-2">
          <span>Setup Progress</span>
          <span>
            Step {currentStep} of {totalSteps}
          </span>
        </div>
        <div className="w-full bg-slate-900 rounded-full h-1 overflow-hidden">
          <div
            className="bg-blue-500 h-full rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
      </div>

      <main className="flex-1 flex items-center justify-center">
        {currentStep === 1 && renderWelcome()}
        {currentStep === 2 && renderConcepts()}
        {currentStep === 3 && renderOrgForm()}
        {currentStep === 4 && renderAppForm()}
        {currentStep === 5 && renderCredentials()}
        {currentStep === 6 && renderComplete()}
      </main>

      <footer className="max-w-md w-full mx-auto text-center px-6">
        <p className="text-[10px] text-slate-600 tracking-wider">
          DPDP Consent Engine · Secure Cloudflare Architecture
        </p>
      </footer>
    </div>
  );
}
